/**
 * The shared viewer, wired to an output folder.
 *
 * Unlike the case viewer (`CaseSharedViewer`), there is no case manifest to
 * key selection against here — just whatever `.nii.gz` files sit beside the
 * one that was clicked — so base/overlay selection is local state, reset to
 * "just the clicked file, no overlays" whenever the clicked file changes.
 */

import * as React from "react";

import { getAssetUrl } from "@/ipc/api";
import type { OutputNode } from "@/ipc/contract";
import { EmptyState } from "@/components/ui/primitives";
import { SharedVolumeViewer, type SharedVolumeFile, type ViewerMode } from "@/viewer/SharedVolumeViewer";
import { useResolvedVolumeUrls } from "@/viewer/useResolvedVolumeUrls";
import { VolumePreview } from "./VolumePreview";

export function OutputVolumeViewer({
  siblings,
  sourceRoot,
  initialBaseRelPath,
}: {
  /** Every NIfTI file in the same folder as the one that was clicked. */
  siblings: OutputNode[];
  /** `sources[i].path` from the browser — siblings' `relPath` is relative to it. */
  sourceRoot: string;
  initialBaseRelPath: string;
}) {
  const files = React.useMemo<SharedVolumeFile[]>(
    () =>
      siblings.map((node) => ({
        id: node.relPath,
        label: node.name,
        resolve: async () => {
          const abs = `${sourceRoot.replace(/[\\/]+$/, "")}/${node.relPath.replace(/^[\\/]+/, "")}`;
          const { url } = await getAssetUrl(abs);
          return url || null;
        },
      })),
    [siblings, sourceRoot],
  );

  const [baseId, setBaseId] = React.useState(initialBaseRelPath);
  const [overlayIds, setOverlayIds] = React.useState<string[]>([]);
  const [mode, setMode] = React.useState<ViewerMode>("slices");

  // A different file was clicked in the tree: start fresh rather than keeping
  // an overlay selection that may not even share this file's folder.
  React.useEffect(() => {
    setBaseId(initialBaseRelPath);
    setOverlayIds([]);
  }, [initialBaseRelPath]);

  const toggleOverlay = (id: string) =>
    setOverlayIds((ids) => (ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]));

  const { baseUrl, overlays } = useResolvedVolumeUrls(files, baseId, overlayIds);
  const baseLabel = files.find((f) => f.id === baseId)?.label ?? "";

  return (
    <SharedVolumeViewer
      files={files}
      baseId={baseId}
      overlayIds={overlayIds}
      onBaseChange={setBaseId}
      onOverlayToggle={toggleOverlay}
      baseUrl={baseUrl}
      overlays={overlays}
      mode={mode}
      onModeChange={setMode}
      renderNiivue={() =>
        baseUrl ? (
          <VolumePreview url={baseUrl} name={baseLabel} overlayUrl={overlays[0]?.url ?? null} />
        ) : (
          <div className="flex h-full items-center justify-center">
            <EmptyState title="Loading…" />
          </div>
        )
      }
    />
  );
}
